import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IBackfillCartCommissionsDTO, { IBackfillCartCommissionsResult } from "../models/DTO/IBackfillCartCommissionsDTO";

// Deadline for one backfill batch (up to 1000 orders). Without it a stalled request would leave the
// caller's run, and the UI it locks, hung forever.
export const BACKFILL_REQUEST_TIMEOUT_MS = 120000;

const readErrorMessage = async (response: Response): Promise<string | null> => {
    try {
        const body = await response.json();
        return body && typeof body.message === "string" && body.message.length ? body.message : null;
    } catch {
        return null;
    }
};

const backfillCommissions = async (
    payload: IBackfillCartCommissionsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IBackfillCartCommissionsResult> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BACKFILL_REQUEST_TIMEOUT_MS);
    try {
        const response = await fetch(buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.CartBackfillCommissions }), {
            method: "POST",
            headers: buildAuthHeadersForFetch({ userModel, supplierModel }),
            body: JSON.stringify(payload),
            signal: controller.signal,
        });
        if (!response.ok) {
            const message = await readErrorMessage(response);
            throw new Error(message ?? `Failed to backfill cart commissions (${response.status}): ${response.statusText}`);
        }
        // Body streaming is covered by the same deadline; the timer is cleared only after it is consumed.
        return await response.json();
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error(`Backfilling cart commissions timed out after ${BACKFILL_REQUEST_TIMEOUT_MS / 1000} seconds.`);
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
};

const CartCommissionService = {
    backfillCommissions,
};

export default CartCommissionService;
